const queues = new Map();
const keyFor = (auth, sessionId) => {
	if (!auth.currentUser?.uid || !sessionId) throw new Error("Sign in before checkout.");
	return `scerv.browserCheckout.v2:${JSON.stringify([auth.app.options.projectId, auth.currentUser.uid, sessionId])}`;
};
const read = key => {
	const raw = window.localStorage.getItem(key);
	if (!raw) return null;
	const value = JSON.parse(raw);
	if (value.version !== 2 || !value.commandId || !value.payload) throw new Error("Pending checkout cannot be read. Contact support before paying again.");
	return value;
};
const assertCommand = (key, commandId) => {
	if ((read(key)?.commandId || null) !== commandId) throw new Error("Pending checkout changed in another tab. Close other checkout tabs and retry to resume the saved payment.");
};
const write = (key, record, previousCommandId = record.commandId) => {
	assertCommand(key, previousCommandId);
	window.localStorage.setItem(key, JSON.stringify(record));
};
const remove = (key, commandId) => {
	assertCommand(key, commandId);
	window.localStorage.removeItem(key);
};
const withBrowserLock = async (key, action) => {
	const locks = window.navigator?.locks;
	const unavailable = () => new Error("Secure checkout synchronization is unavailable. Update your browser and reopen this HTTPS page, then retry. Ask staff to check any pending payment before paying another way.");
	if (typeof locks?.request !== "function") throw unavailable();
	let entered = false;
	try {
		// The browser holds this exclusive lock across tabs and awaited provider responses.
		return await locks.request(key, { mode: "exclusive", ifAvailable: true }, lock => {
			entered = true;
			if (!lock) throw new Error("Checkout is active in another tab. Close other checkout tabs and retry here to resume the saved payment.");
			return action();
		});
	} catch (error) {
		if (!entered) throw unavailable();
		throw error;
	}
};
const serial = (key, action) => {
	const promise = (queues.get(key) || Promise.resolve()).catch(() => {}).then(() => withBrowserLock(key, action));
	queues.set(key, promise);
	promise.finally(() => { if (queues.get(key) === promise) queues.delete(key); }).catch(() => {});
	return promise;
};
const intent = payload => JSON.stringify([payload.sessionId, [...payload.lineIds].sort(), payload.gratuity]);
const newId = () => [...window.crypto.getRandomValues(new Uint8Array(24))].map(byte => byte.toString(16).padStart(2, "0")).join("");

export function prepareBrowserCheckout({ auth, invoke, payload }) {
	const key = keyFor(auth, payload.sessionId);
	return serial(key, async () => {
		const current = () => { if (keyFor(auth, payload.sessionId) !== key) throw new Error("Your account changed. Sign back in to resume checkout."); };
		current();
		let record = read(key);
		if (!record) {
			try {
				const result = await invoke("createBrowserCheckoutSession", payload);
				current();
				return result;
			}
			catch (error) { if (error?.details?.reason !== "checkout_upgrade_required") throw error; }
			current();
		}
		if (record && intent(record.payload) !== intent(payload)) {
			if (!window.confirm("Cancel the pending checkout before paying for a different selection or tip?")) throw new Error("The original checkout is still reserved.");
			current();
			const cancelled = await invoke("cancelBrowserCheckoutSessionV2", { ...record.payload, commandId: record.commandId });
			if (cancelled.status !== "cancelled") throw new Error("Cancellation is not confirmed. Resume the original checkout.");
			current();
			remove(key, record.commandId);
			record = null;
		}
		if (!record) {
			record = { version: 2, commandId: newId(), payload };
			write(key, record, null);
		}
		current();
		let result;
		try { result = await invoke("createBrowserCheckoutSessionV2", { ...record.payload, commandId: record.commandId }); }
		catch (error) {
			const quote = error?.details?.quote;
			if (error?.details?.reason !== "quote_changed" || !Number.isSafeInteger(quote?.total)) throw error;
			current();
			const dollars = cents => `$${(cents / 100).toFixed(2)}`;
			if (!window.confirm(`Review payment\nSubtotal ${dollars(quote.subtotal)}\nTax ${dollars(quote.taxAmount)}\nTip ${dollars(quote.gratuity)}\nService fee ${dollars(quote.platformFee)}\nTotal ${dollars(quote.total)}`)) {
				current();
				remove(key, record.commandId);
				throw new Error("Review checkout before paying.");
			}
			current();
			record.payload = { ...record.payload, expectedTotal: quote.total };
			write(key, record);
			result = await invoke("createBrowserCheckoutSessionV2", { ...record.payload, commandId: record.commandId });
		}
		current();
		if (result.paymentStatus === "cancelled") {
			remove(key, record.commandId);
			throw new Error("Checkout was cancelled. Review your items before paying again.");
		}
		record.orderId = result.orderId;
		record.checkoutSessionId = result.checkoutSessionId;
		write(key, record);
		if (result.paymentStatus === "paid") return { ...result, checkoutUrl: `${payload.returnUrl}?payment=success&orderId=${encodeURIComponent(result.orderId)}&checkout_session_id=${encodeURIComponent(result.checkoutSessionId)}` };
		return result;
	});
}

export function cancelBrowserCheckout({ auth, invoke, sessionId, expectedOrderId }) {
	const key = keyFor(auth, sessionId);
	return serial(key, async () => {
		if (keyFor(auth, sessionId) !== key) throw new Error("Your account changed. Sign back in to resume checkout.");
		const record = read(key);
		if (!record || !expectedOrderId || record.orderId !== expectedOrderId) return;
		const result = await invoke("cancelBrowserCheckoutSessionV2", { ...record.payload, commandId: record.commandId });
		if (keyFor(auth, sessionId) !== key || result.status !== "cancelled") throw new Error("Cancellation is not confirmed.");
		remove(key, record.commandId);
	});
}

export function acknowledgeBrowserCheckout({ auth, sessionId, result }) {
	if (result?.status !== "paid" || !result.orderId || !sessionId) return Promise.resolve(false);
	let key;
	try { key = keyFor(auth, sessionId); } catch (_) { return Promise.resolve(false); }
	// Callers need not await best-effort cleanup; any failure retains recovery data.
	return serial(key, async () => {
		if (keyFor(auth, sessionId) !== key) throw new Error("Your account changed. Sign back in to resume checkout.");
		const record = read(key);
		if (!record || record.orderId !== result.orderId) return false;
		remove(key, record.commandId);
		return true;
	}).catch(() => false);
}
